import { useMemo, useState } from 'react';
import { Checkbox, FormControl, FormControlLabel, FormGroup, MenuItem, Select, TextField, Theme } from '@material-ui/core';
import makeStyles from '@material-ui/styles/makeStyles';
import Modal from 'components/shared/Modal';
import { AzureUser } from 'utils/wemble-api.generated';
import { SettingsButtons } from 'components/shared/Settings';
import { useCreateMultipleUsersMutation, useGetAzureUsersQuery } from 'utils/wemble-api';
import OptionsComponent from 'components/users/OptionsComponent';
import { notEmpty } from 'utils/helpers';

const useStyles = makeStyles((theme: Theme) => ({
  listItem: {
    borderBottom: 'solid 1px',
    borderBottomColor: theme.palette.gray.g7,
    marginBlock: 0,
    userSelect: 'none',
  },
  label: {
    width: '100%',
  },
  checkBox: {
    color: theme.palette.gray.g1,
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
    '&.MuiCheckbox-colorPrimary.Mui-checked': {
      color: theme.palette.gray.g1,
    },
  },
  list__user: {
    display: 'flex',
    alignItems: 'center',
    paddingBlock: 8,
    margin: 0,
  },
  photoCircle: {
    borderRadius: '50%',
    overflow: 'hidden',
    width: 44,
    height: 44,
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.gray.g6,
    color: theme.palette.gray.g1,
    fontWeight: 600,
    fontSize: 14,
  },
  userText: {
    padding: '2px 0 2px 10px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    color: theme.palette.gray.g1,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '115%',
  },
  userSubtitle: {
    fontSize: 10,
    marginTop: -1,
    color: '#828282',
  },
}));

const InviteADUsersModal = ({
  open,
  onClose,
  azureADTenantId,
}: {
  open: boolean;
  onClose: () => void;
  azureADTenantId: string | undefined;
}) => {
  const classes = useStyles();
  const [selectedUsers, setSelectedUsers] = useState<AzureUser[]>([]);
  const [search, setSearch] = useState('');
  const [titleFilter, setTitleFilter] = useState<string>('');
  const { data: users, refetch } = useGetAzureUsersQuery(undefined, {
    skip: typeof azureADTenantId !== 'string',
  });

  // Unique titles from the fetched users, sorted by frequency
  const titleOptions = useMemo(() => {
    if (!users) return [];
    const counts = new Map<string, number>();
    for (const u of users) {
      if (u.jobTitle) counts.set(u.jobTitle, (counts.get(u.jobTitle) || 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [users]);

  const MAX_VISIBLE = 30;
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (titleFilter && u.jobTitle !== titleFilter) return false;
      if (!q) return true;
      return (u.displayName || '').toLowerCase().includes(q) ||
        (u.mail || '').toLowerCase().includes(q);
    });
  }, [users, search, titleFilter]);
  const visibleUsers = filteredUsers.slice(0, MAX_VISIBLE);
  const hiddenCount = Math.max(0, filteredUsers.length - MAX_VISIBLE);
  const [createMultipleUsers, { isLoading }] = useCreateMultipleUsersMutation();
  const [addAsAdmin, setAddAsAdmin] = useState(false);

  const [fields, setFields] = useState({
    group: '',
    office: '',
    secondaryGroup: '',
    secondaryOffice: '',
  });
  const [secondaryEnabled, setSecondaryEnabled] = useState(
    fields.secondaryGroup.length > 0 && fields.secondaryOffice.length > 0,
  );

  const handleUserSelect = (selectedUser: AzureUser, checked: boolean) => {
    const users = selectedUsers.filter((user) => user.mail !== selectedUser.mail);
    if (checked) {
      users.push(selectedUser);
    }
    setSelectedUsers(users);
  };

  const handleUpdateField = (field: keyof typeof fields) => (value: string) => {
    setFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onSubmit = async () => {
    if (isLoading) return;
    await createMultipleUsers({
      multipleUserCreationParams: {
        users: selectedUsers
          .map((user) =>
            user.mail
              ? {
                email: user.mail,
                name: user.displayName ?? '',
                azureADId: user.id ?? undefined,
              }
              : null,
          )
          .filter(notEmpty),
        admin: addAsAdmin,
        group: fields.group ?? undefined,
        office: fields.office ?? undefined,
        secondaryEnabled,
        secondaryOffice: secondaryEnabled
          ? fields.secondaryOffice.length > 0
            ? fields.secondaryOffice
            : undefined
          : undefined,
        secondaryGroup: secondaryEnabled
          ? fields.secondaryGroup.length > 0
            ? fields.secondaryGroup
            : undefined
          : undefined,
      },
    });
    refetch();
    onClose();
    setSelectedUsers([]);
    setFields({
      group: '',
      office: '',
      secondaryGroup: '',
      secondaryOffice: '',
    });
  };

  return (
    <Modal visible={open} close={onClose} customWidth={600}>
      <h5>Primary group</h5>
      <div
        style={{
          width: '100%',
          float: 'left',
          marginTop: -15,
        }}
      >
        <OptionsComponent
          selectedOfficeId={fields.office}
          selectedGroupId={fields.group}
          setSelectedOffice={handleUpdateField('office')}
          setSelectedGroup={handleUpdateField('group')}
        />
      </div>

      <h5 style={{ marginTop: 60 + 'px', marginBottom: -15 + 'px' }}>Secondary group</h5>

      <div className="form-check" style={{ paddingLeft: 23 + 'px' }}>
        <div className=" position-static">
          <input
            className="form-check-input "
            style={{
              marginRight: '6px',
              marginTop: '26px',
            }}
            type="checkbox"
            checked={secondaryEnabled}
            onChange={() => setSecondaryEnabled((prev) => !prev)}
          />
        </div>

        <div
          style={{
            opacity: secondaryEnabled ? 1.0 : 0.75,
            filter: secondaryEnabled ? '' : 'saturate(0)',
            transition: '0.1s ease-in-out',
            pointerEvents: secondaryEnabled ? 'auto' : 'none',
            marginBottom: 20,
          }}
        >
          <OptionsComponent
            secondary
            selectedOfficeId={fields.secondaryOffice}
            selectedGroupId={fields.secondaryGroup}
            setSelectedOffice={handleUpdateField('secondaryOffice')}
            setSelectedGroup={handleUpdateField('secondaryGroup')}
            filterGroup={(group) => group._id !== fields.group}
            filterOffice={(office) => office._id !== fields.office}
          />
        </div>
      </div>
      <FormControlLabel
        className={classes.listItem}
        value="end"
        classes={{ label: classes.label }}
        control={
          <Checkbox
            color="primary"
            className={classes.checkBox}
            disableFocusRipple
            disableRipple
            disableTouchRipple
            checked={addAsAdmin}
            onChange={() => setAddAsAdmin((prev) => !prev)}
          />
        }
        style={{
          paddingBottom: 20,
        }}
        label="Add as administrator"
        labelPlacement="end"
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <TextField
          placeholder={`Search ${users?.length ?? ''} users by name or email...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="small"
          style={{ flex: 1 }}
        />
        {titleOptions.length > 0 && (
          <Select
            value={titleFilter}
            onChange={(e) => setTitleFilter(e.target.value as string)}
            displayEmpty
            variant="outlined"
            style={{ minWidth: 160, height: 40 }}
          >
            <MenuItem value="">All titles</MenuItem>
            {titleOptions.map(([title, count]) => (
              <MenuItem key={title} value={title}>{title} ({count})</MenuItem>
            ))}
          </Select>
        )}
      </div>
      <FormControl component="fieldset" fullWidth style={{ maxHeight: 360, overflowY: 'auto' }}>
        <FormGroup aria-label="position">
          {visibleUsers.map((user, key) => {
            const isSelected = selectedUsers.some(u => u.mail === user.mail);
            return (
              <FormControlLabel
                className={classes.listItem}
                key={key}
                value="end"
                classes={{ label: classes.label }}
                control={
                  <Checkbox
                    color="primary"
                    className={classes.checkBox}
                    disabled={!Boolean(user.mail)}
                    checked={isSelected}
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    onChange={(event) => {
                      handleUserSelect(user, event.target.checked);
                    }}
                  />
                }
                label={
                  <div className={classes.list__user}>
                    <div className={classes.photoCircle}>
                      {user.displayName?.charAt(0)}
                    </div>
                    <div className={classes.userText}>
                      <div>{user.displayName}</div>
                      <div className={classes.userSubtitle}>
                        {user.mail
                          ? (user.jobTitle ? `${user.jobTitle} · ${user.mail}` : user.mail)
                          : `No email set in Entra — can't be added until configured`}
                      </div>
                    </div>
                  </div>
                }
                labelPlacement="end"
              />
            );
          })}
          {hiddenCount > 0 && (
            <div style={{ padding: '12px 0', textAlign: 'center', color: '#888', fontSize: 13 }}>
              {hiddenCount} more — refine your search to see them
            </div>
          )}
          {filteredUsers.length === 0 && users && users.length > 0 && (
            <div style={{ padding: '12px 0', textAlign: 'center', color: '#888', fontSize: 13 }}>
              No users match "{search}"
            </div>
          )}
        </FormGroup>
      </FormControl>
      <SettingsButtons
        discardCustom={'Cancel'}
        discard={onClose}
        saveCustom={selectedUsers.length > 0 ? `Add ${selectedUsers.length} Selected ${selectedUsers.length === 1 ? 'User' : 'Users'}` : 'Add Selected Users'}
        save={onSubmit}
        customPadding={15}
      />
    </Modal>
  );
};

export default InviteADUsersModal;
